import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MicrophoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDurationMMSS } from '@/util/utils';
const THINKING_AUDIO_THRESHOLD = 0.04;
const THINKING_DELAY_MS = 1400;
const MAX_THINKING_DURATION_MS = 4000;
const VoiceWaveIndicator = ({ audioLevel, isMuted = false }) => {
    const prevLevelRef = useRef(0);
    const [smoothedLevel, setSmoothedLevel] = useState(0);
    useEffect(() => {
        // Exponential smoothing: 0.3 weight for new value, 0.7 for previous
        const smoothingFactor = 0.3;
        const targetLevel = audioLevel * 5;
        const newSmoothed = targetLevel * smoothingFactor +
            prevLevelRef.current * (1 - smoothingFactor);
        const changeThreshold = 0.02;
        if (Math.abs(newSmoothed - prevLevelRef.current) > changeThreshold ||
            audioLevel < 0.005) {
            prevLevelRef.current = newSmoothed;
            setSmoothedLevel(newSmoothed);
        }
    }, [audioLevel]);
    const normalizedLevel = Math.min(1, Math.max(0, smoothedLevel));
    const multipliers = [0.4, 0.7, 1.0, 0.7, 0.4];
    const isActive = smoothedLevel > 0.03;
    return (_jsx("div", { className: "flex h-9 w-9 items-center justify-center gap-[2px]", children: multipliers.map((multiplier, index) => {
            const scale = isActive
                ? Math.max(0.25, Math.min(0.95, 0.25 + normalizedLevel * multiplier * 0.7))
                : 0.15;
            return (_jsx("span", { className: `h-5 w-[2px] origin-center rounded-full transition-transform duration-150 ${isMuted ? 'bg-slate-400' : 'bg-emerald-500'}`, style: {
                    transform: `scaleY(${scale})`,
                    opacity: isActive ? 0.7 + normalizedLevel * 0.3 : 0.4,
                } }, index));
        }) }));
};
const PhoneIcon = ({ className = '' }) => (_jsx("svg", { className: `h-[18px] w-[18px] ${className}`, viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" }) }));
const ErrorIcon = () => (_jsx("svg", { className: "h-[18px] w-[18px]", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }));
const MutedMicrophoneIcon = () => (_jsxs("div", { className: "relative h-5 w-5", children: [_jsx(MicrophoneIcon, { className: "h-5 w-5" }), _jsx("svg", { className: "absolute inset-0 h-5 w-5", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M3 3L17 17", stroke: "currentColor", strokeWidth: 2.5, strokeLinecap: "round" }) })] }));
const DialpadIcon = () => (_jsxs("svg", { className: "h-5 w-5", viewBox: "0 0 24 24", fill: "currentColor", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "6", cy: "5", r: "1.5" }), _jsx("circle", { cx: "12", cy: "5", r: "1.5" }), _jsx("circle", { cx: "18", cy: "5", r: "1.5" }), _jsx("circle", { cx: "6", cy: "10", r: "1.5" }), _jsx("circle", { cx: "12", cy: "10", r: "1.5" }), _jsx("circle", { cx: "18", cy: "10", r: "1.5" }), _jsx("circle", { cx: "6", cy: "15", r: "1.5" }), _jsx("circle", { cx: "12", cy: "15", r: "1.5" }), _jsx("circle", { cx: "18", cy: "15", r: "1.5" }), _jsx("circle", { cx: "12", cy: "20", r: "1.5" })] }));
const DTMF_KEYS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
];
const DtmfKeypad = ({ onSendDigits }) => {
    const { t } = useTranslation();
    const [pressedKey, setPressedKey] = useState(null);
    const handlePress = (digit) => {
        onSendDigits(digit);
        setPressedKey(digit);
        setTimeout(() => setPressedKey(null), 150);
    };
    return (_jsx("div", { className: "grid grid-cols-3 gap-2 rounded-xl border border-slate-200/80 bg-white/90 p-3 shadow-sm backdrop-blur-md dark:border-slate-700/50 dark:bg-slate-900/80", role: "group", "aria-label": t('audio_widget.dtmf.label'), children: DTMF_KEYS.flat().map((digit) => (_jsx("button", { type: "button", onClick: () => handlePress(digit), className: `flex h-11 w-11 items-center justify-center rounded-lg text-base font-semibold transition-all duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 active:scale-95 ${pressedKey === digit
                ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700/50 dark:text-slate-200 dark:hover:bg-slate-700'}`, "aria-label": digit, children: digit }, digit))) }));
};
const AudioWidget = ({ onStartCall, onEndCall, onToggleMute, isMuted, onSendDigits, onEvent, }) => {
    const { t } = useTranslation();
    const [callState, setCallState] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');
    const [audioLevel, setAudioLevel] = useState(0);
    const [isThinking, setIsThinking] = useState(false);
    const [hasUserSpoken, setHasUserSpoken] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [isMutedState, setIsMutedState] = useState(false);
    const [showDialpad, setShowDialpad] = useState(false);
    const warningTimeoutRef = useRef(null);
    const thinkingTimeoutRef = useRef(null);
    const thinkingAutoClearRef = useRef(null);
    const callStartTimeRef = useRef(null);
    const durationIntervalRef = useRef(null);
    useEffect(() => {
        if (callState === 'connected' && callStartTimeRef.current) {
            durationIntervalRef.current = window.setInterval(() => {
                if (callStartTimeRef.current) {
                    setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
                }
            }, 1000);
        }
        else if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        };
    }, [callState]);
    useEffect(() => {
        const handleStateUpdate = ({ state, message, data, }) => {
            switch (state) {
                case 'audio-level': {
                    const source = data !== null && data !== void 0 ? data : message;
                    let parsedLevel;
                    if (typeof source === 'number') {
                        parsedLevel = source;
                    }
                    else if (typeof source === 'string') {
                        parsedLevel = parseFloat(source);
                    }
                    else {
                        parsedLevel = Number(source);
                    }
                    if (!Number.isNaN(parsedLevel)) {
                        setAudioLevel(Math.max(0, parsedLevel));
                    }
                    break;
                }
                case 'warning':
                    if (message) {
                        setWarningMessage(message);
                        if (warningTimeoutRef.current) {
                            clearTimeout(warningTimeoutRef.current);
                        }
                        warningTimeoutRef.current = window.setTimeout(() => {
                            setWarningMessage('');
                            warningTimeoutRef.current = null;
                        }, 5000);
                    }
                    break;
                case 'warning-cleared':
                    setWarningMessage('');
                    if (warningTimeoutRef.current) {
                        clearTimeout(warningTimeoutRef.current);
                        warningTimeoutRef.current = null;
                    }
                    break;
                case 'remote-audio':
                    if (thinkingTimeoutRef.current) {
                        clearTimeout(thinkingTimeoutRef.current);
                        thinkingTimeoutRef.current = null;
                    }
                    if (thinkingAutoClearRef.current) {
                        clearTimeout(thinkingAutoClearRef.current);
                        thinkingAutoClearRef.current = null;
                    }
                    setIsThinking(false);
                    setHasUserSpoken(false);
                    break;
                case 'error':
                    setCallState('error');
                    setErrorMessage(message || t('audio_widget.errors.generic'));
                    setIsThinking(false);
                    setHasUserSpoken(false);
                    setCallDuration(0);
                    callStartTimeRef.current = null;
                    if (thinkingAutoClearRef.current) {
                        clearTimeout(thinkingAutoClearRef.current);
                        thinkingAutoClearRef.current = null;
                    }
                    setTimeout(() => {
                        setCallState('idle');
                        setErrorMessage('');
                    }, 3000);
                    break;
            }
        };
        const unsubscribe = onEvent('any', handleStateUpdate);
        return () => {
            unsubscribe();
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
            }
        };
    }, [onEvent, t]);
    useEffect(() => {
        if (callState !== 'connected') {
            if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
                thinkingTimeoutRef.current = null;
            }
            if (thinkingAutoClearRef.current) {
                clearTimeout(thinkingAutoClearRef.current);
                thinkingAutoClearRef.current = null;
            }
            // Defer state updates to avoid synchronous setState in effect
            queueMicrotask(() => {
                if (isThinking) {
                    setIsThinking(false);
                }
                if (hasUserSpoken) {
                    setHasUserSpoken(false);
                }
            });
            return;
        }
        if (audioLevel > THINKING_AUDIO_THRESHOLD) {
            // Defer state updates to avoid synchronous setState in effect
            queueMicrotask(() => {
                if (!hasUserSpoken) {
                    setHasUserSpoken(true);
                }
                if (isThinking) {
                    setIsThinking(false);
                }
            });
            if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
                thinkingTimeoutRef.current = null;
            }
            if (thinkingAutoClearRef.current) {
                clearTimeout(thinkingAutoClearRef.current);
                thinkingAutoClearRef.current = null;
            }
            return;
        }
        if (hasUserSpoken && !isThinking && !thinkingTimeoutRef.current) {
            thinkingTimeoutRef.current = window.setTimeout(() => {
                setIsThinking(true);
                thinkingTimeoutRef.current = null;
            }, THINKING_DELAY_MS);
        }
    }, [audioLevel, callState, hasUserSpoken, isThinking]);
    useEffect(() => {
        return () => {
            if (thinkingTimeoutRef.current) {
                clearTimeout(thinkingTimeoutRef.current);
            }
            if (thinkingAutoClearRef.current) {
                clearTimeout(thinkingAutoClearRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (isThinking) {
            if (thinkingAutoClearRef.current) {
                clearTimeout(thinkingAutoClearRef.current);
            }
            thinkingAutoClearRef.current = window.setTimeout(() => {
                setIsThinking(false);
                setHasUserSpoken(false);
                thinkingAutoClearRef.current = null;
            }, MAX_THINKING_DURATION_MS);
        }
        else if (thinkingAutoClearRef.current) {
            clearTimeout(thinkingAutoClearRef.current);
            thinkingAutoClearRef.current = null;
        }
    }, [isThinking]);
    const handleStartCall = async () => {
        try {
            setCallState('connecting');
            setErrorMessage('');
            setAudioLevel(0);
            setIsThinking(false);
            setHasUserSpoken(false);
            setCallDuration(0);
            callStartTimeRef.current = null;
            await onStartCall();
            setCallState('connected');
            callStartTimeRef.current = Date.now();
            setCallDuration(0);
        }
        catch (error) {
            setCallState('error');
            setIsThinking(false);
            setHasUserSpoken(false);
            setErrorMessage(error instanceof Error
                ? error.message
                : t('audio_widget.errors.start_failed'));
            setCallDuration(0);
            callStartTimeRef.current = null;
        }
    };
    const handleEndCall = () => {
        if (thinkingTimeoutRef.current) {
            clearTimeout(thinkingTimeoutRef.current);
            thinkingTimeoutRef.current = null;
        }
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        onEndCall();
        setCallState('idle');
        setAudioLevel(0);
        setIsThinking(false);
        setHasUserSpoken(false);
        setCallDuration(0);
        setIsMutedState(false);
        setShowDialpad(false);
        callStartTimeRef.current = null;
        if (thinkingAutoClearRef.current) {
            clearTimeout(thinkingAutoClearRef.current);
            thinkingAutoClearRef.current = null;
        }
    };
    const handleToggleMute = () => {
        onToggleMute();
        setIsMutedState(isMuted());
    };
    const showSpinner = callState === 'connecting';
    const buttonToneClass = callState === 'error'
        ? 'bg-rose-50/80 text-rose-600 border border-rose-200/60 shadow-[0_2px_8px_rgba(244,63,94,0.12)] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30'
        : callState === 'connected'
            ? 'bg-emerald-50/80 text-emerald-600 border border-emerald-200/60 shadow-[0_2px_8px_rgba(16,185,129,0.15)] hover:bg-gradient-to-br hover:from-red-500 hover:to-red-600 hover:text-white hover:border-red-500/20 hover:shadow-[0_6px_20px_rgba(239,68,68,0.35)] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30 dark:hover:from-red-600 dark:hover:to-red-700 transition-colors'
            : callState === 'connecting'
                ? 'bg-sky-50/80 text-sky-600 border border-sky-200/60 shadow-[0_2px_8px_rgba(14,165,233,0.12)] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/30'
                : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border border-emerald-500/20 shadow-[0_4px_14px_rgba(16,185,129,0.25)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.35)] dark:from-emerald-600 dark:to-emerald-700 dark:border-emerald-500/30';
    const spinnerBorderClass = callState === 'error'
        ? 'border-t-rose-400/60 border-r-rose-300/30 border-b-transparent border-l-transparent'
        : isThinking
            ? 'border-t-sky-400/60 border-r-sky-300/30 border-b-transparent border-l-transparent'
            : 'border-t-sky-500/60 border-r-sky-400/30 border-b-transparent border-l-transparent';
    const statusLabel = callState === 'idle'
        ? t('audio_widget.status.ready')
        : callState === 'connecting'
            ? t('audio_widget.status.connecting')
            : callState === 'connected'
                ? isThinking
                    ? t('audio_widget.status.thinking')
                    : t('audio_widget.status.on_call')
                : t('audio_widget.status.error');
    const statusToneClass = callState === 'error'
        ? 'text-rose-500 dark:text-rose-400'
        : callState === 'connected'
            ? 'text-emerald-500 dark:text-emerald-400'
            : callState === 'connecting'
                ? 'text-sky-500 dark:text-sky-400'
                : 'text-slate-500 dark:text-slate-400';
    const buttonActionLabel = callState === 'idle' || callState === 'error'
        ? t('audio_widget.actions.start')
        : callState === 'connecting'
            ? t('audio_widget.actions.cancel')
            : t('audio_widget.actions.end');
    const srStatus = callState === 'idle'
        ? t('audio_widget.sr.ready')
        : callState === 'connecting'
            ? t('audio_widget.sr.connecting')
            : callState === 'connected'
                ? isThinking
                    ? t('audio_widget.sr.connected_thinking')
                    : t('audio_widget.sr.connected')
                : errorMessage
                    ? t('audio_widget.sr.error_with_message', { message: errorMessage })
                    : t('audio_widget.sr.error');
    const handlePrimaryAction = () => {
        if (callState === 'idle' || callState === 'error') {
            void handleStartCall();
            return;
        }
        handleEndCall();
    };
    return (_jsxs("div", { className: "relative font-sans text-slate-800 antialiased dark:text-slate-100", children: [warningMessage && (_jsx("div", { className: "absolute -top-14 left-1/2 w-max max-w-[18rem] -translate-x-1/2 rounded-full bg-amber-100/90 px-4 py-2 text-xs font-medium text-amber-800 shadow-lg shadow-amber-200/70 ring-1 ring-amber-300 backdrop-blur dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-400/40", role: "status", "aria-live": "polite", children: warningMessage })), _jsxs("div", { className: "inline-flex min-w-[260px] items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/90 px-5 py-3.5 shadow-sm backdrop-blur-md backdrop-saturate-150 transition-all duration-200 hover:shadow-md dark:border-slate-700/50 dark:bg-slate-900/80 dark:shadow-xl dark:shadow-slate-950/30", children: [_jsxs("button", { type: "button", onClick: handlePrimaryAction, className: `group relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:scale-[1.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.98] dark:focus-visible:ring-offset-slate-950 ${buttonToneClass}`, "aria-label": buttonActionLabel, title: callState === 'error' && errorMessage ? errorMessage : statusLabel, children: [_jsx("span", { className: "sr-only", children: srStatus }), callState === 'connected' ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0", children: _jsx(VoiceWaveIndicator, { audioLevel: audioLevel, isMuted: isMutedState }) }), _jsx("div", { className: "absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100", children: _jsx(PhoneXMarkIcon, { className: "h-5 w-5" }) })] })) : callState === 'error' ? (_jsx(ErrorIcon, {})) : (_jsx(PhoneIcon, { className: callState === 'idle'
                                    ? 'fill-white'
                                    : 'fill-sky-600 dark:fill-sky-300' })), showSpinner ? (_jsx("span", { className: `pointer-events-none absolute inset-[-4px] rounded-full border border-transparent ${spinnerBorderClass} animate-spin` })) : null] }), callState === 'connected' && (_jsx("button", { type: "button", onClick: handleToggleMute, className: `flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 active:scale-95 ${isMutedState
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 focus-visible:ring-amber-400 dark:bg-amber-500/20 dark:text-amber-300 dark:hover:bg-amber-500/30'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 focus-visible:ring-slate-400 dark:bg-slate-700/50 dark:text-slate-300 dark:hover:bg-slate-700'}`, "aria-label": isMutedState
                            ? t('audio_widget.mute.unmute_microphone')
                            : t('audio_widget.mute.mute_microphone'), title: isMutedState
                            ? t('audio_widget.mute.unmute')
                            : t('audio_widget.mute.mute'), children: isMutedState ? (_jsx(MutedMicrophoneIcon, {})) : (_jsx(MicrophoneIcon, { className: "h-5 w-5" })) })), callState === 'connected' && (_jsxs("div", { className: "relative shrink-0", children: [_jsx("button", { type: "button", onClick: () => setShowDialpad((prev) => !prev), className: `flex h-12 w-12 items-center justify-center rounded-full transition-all duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 active:scale-95 ${showDialpad
                                    ? 'bg-sky-100 text-sky-700 hover:bg-sky-200 focus-visible:ring-sky-400 dark:bg-sky-500/20 dark:text-sky-300 dark:hover:bg-sky-500/30'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 focus-visible:ring-slate-400 dark:bg-slate-700/50 dark:text-slate-300 dark:hover:bg-slate-700'}`, "aria-label": t('audio_widget.dtmf.toggle'), title: t('audio_widget.dtmf.toggle'), "aria-expanded": showDialpad, children: _jsx(DialpadIcon, {}) }), showDialpad && (_jsx("div", { className: "absolute bottom-full left-1/2 mb-5 w-max -translate-x-1/2", children: _jsx(DtmfKeypad, { onSendDigits: onSendDigits }) }))] })), _jsxs("div", { className: "relative flex flex-col gap-1 overflow-hidden leading-tight", children: [_jsx("span", { className: `relative z-10 text-[0.65rem] font-medium uppercase tracking-[0.25em] ${statusToneClass} transition-colors duration-200`, children: statusLabel }), callState === 'connected' && (_jsx("span", { className: "animate-in fade-in slide-in-from-bottom-1 relative z-10 font-mono text-[0.8rem] text-slate-600 transition-all duration-500 dark:text-slate-400", children: formatDurationMMSS(callDuration) }))] })] })] }));
};
export { AudioWidget };
